import moment from 'moment';

const normalizeRange = (range) => ({
  start: moment(range.start),
  end: moment(range.end),
});

const getFilterRange = (store) => store.getters['filter/date'];

// Rentang [lastPulse - hours, lastPulse] dari data pulse terakhir aset, biar window
// ngikut data terakhir (bukan jam sekarang). Null kalau belum ada data pulse.
const anchorRangeToLastData = (store, massId, hours) => {
  const assets = store.getters['asset/assets'] || [];
  const asset = assets.find((a) => String(a.massId) === String(massId));
  const lastTs = asset?.lastGpsDataPulse?.timestamp || asset?.lastGpsData?.timestamp;
  if (!lastTs) return null;
  const end = moment(lastTs * 1000);
  const start = end.clone().subtract(hours, 'hours');
  return { start, end };
};

const resolveInterval = (store, { interval, allowMinute = true, forceInterval } = {}) => {
  let resolved = interval ?? store.getters['filter/interval'];

  if (forceInterval) {
    resolved = forceInterval;
  } else if (!allowMinute && resolved === 'minute') {
    resolved = 'hour';
  }

  if (resolved !== store.getters['filter/interval']) {
    store.commit('filter/SET_INTERVAL', resolved);
  }

  return resolved;
};

export const initAssetSummary = async (store, options = {}) => {
  const {
    dateRange,
    setFilterDate = false,
    interval,
    allowMinute = true,
    forceInterval,
    withDevices = false,
    withLastData = false,
    splitByWeek = false,
    anchorHoursFromLastData,
  } = options;

  let assets = store.getters['asset/assets'] || [];
  let loadedAssets = false;

  // Always load assets first if empty or if we need to ensure fresh data
  if (assets.length === 0) {
    await store.dispatch('asset/getAssetsData');
    assets = store.getters['asset/assets'] || [];
    loadedAssets = true;
  }

  if (withDevices) {
    const needsDevices = assets.some((asset) => !Array.isArray(asset.devices));
    if (loadedAssets || needsDevices) {
      await store.dispatch('asset/getAssetsDevices');
      assets = store.getters['asset/assets'] || [];
    }
  }

  if (withLastData && loadedAssets) {
    await store.dispatch('asset/getLastData');
    assets = store.getters['asset/assets'] || [];
  }

  let currentMassId = store.getters['filter/currentMassId'];

  if (!currentMassId && assets.length > 0) {
    await store.dispatch('filter/setCurrentMassId', assets[0].massId);
    currentMassId = store.getters['filter/currentMassId'];
  }

  const resolvedInterval = resolveInterval(store, { interval, allowMinute, forceInterval });
  let resolvedRange = dateRange ? normalizeRange(dateRange) : getFilterRange(store);

  if (setFilterDate && dateRange) {
    store.commit('filter/SET_DATE', resolvedRange);
  }

  if (currentMassId) {
    // Verify asset exists before fetching summary
    // Convert to plain array to avoid Vue Observable issues
    const assetsArray = Array.isArray(assets) ? assets : Object.values(assets);
    const currentMassIdStr = String(currentMassId);
    const assetExists = assetsArray.some((asset) => {
      const match = String(asset.massId) === currentMassIdStr;
      return match;
    });

    if (!assetExists) {
      console.error('[initAssetSummary] Asset not found!');
      return {
        assets: store.getters['asset/assets'] || [],
        currentMassId,
        interval: resolvedInterval,
        dateRange: resolvedRange,
      };
    }

    // Window dianchor ke data pulse terakhir aset bila diminta (bukan jam sekarang).
    if (anchorHoursFromLastData) {
      const anchored = anchorRangeToLastData(store, currentMassId, anchorHoursFromLastData);
      if (anchored) resolvedRange = anchored;
    }

    // Align start/end to the resolved interval boundaries to match aggregation buckets
    const fetchStart = moment(resolvedRange.start).startOf(resolvedInterval);
    const fetchEnd = moment(resolvedRange.end).endOf(resolvedInterval);

    await store.dispatch('asset/fetchSummary', {
      start: fetchStart,
      end: fetchEnd,
      interval: resolvedInterval,
      massId: currentMassId,
      splitByWeek,
    });
  } else {
    console.warn('[initAssetSummary] No massId, skipping fetchSummary');
  }

  return {
    assets: store.getters['asset/assets'] || [],
    currentMassId,
    interval: resolvedInterval,
    dateRange: resolvedRange,
  };
};

export const applyAssetSummaryFilter = async (store, options = {}) => {
  const {
    dateRange,
    interval,
    massId,
    setFilterDate = false,
    splitByWeek = false,
    anchorHoursFromLastData,
  } = options;

  if (massId) {
    await store.dispatch('filter/setCurrentMassId', massId);
  }

  if (interval) {
    await store.dispatch('filter/setInterval', interval);
  }

  const resolvedMassId = massId || store.getters['filter/currentMassId'];
  const resolvedInterval = interval || store.getters['filter/interval'];
  let resolvedRange = dateRange ? normalizeRange(dateRange) : getFilterRange(store);

  if (setFilterDate && dateRange) {
    store.commit('filter/SET_DATE', resolvedRange);
  }

  if (resolvedMassId) {
    // Ensure assets are loaded before fetching summary
    let assets = store.getters['asset/assets'] || [];

    if (assets.length === 0) {
      await store.dispatch('asset/getAssetsData');
      assets = store.getters['asset/assets'] || [];
    }

    // Verify asset exists (with string comparison)
    const assetsArray = Array.isArray(assets) ? assets : Object.values(assets);
    const resolvedMassIdStr = String(resolvedMassId);

    // eslint-disable-next-line no-unused-vars
    let foundAsset = null;
    const assetExists = assetsArray.some((asset) => {
      const assetMassIdStr = String(asset.massId);
      const match = assetMassIdStr === resolvedMassIdStr;
      if (match) foundAsset = asset;
      return match;
    });

    if (!assetExists) {
      console.error(`[APPLY FILTER] Asset with massId ${resolvedMassId} not found!`);
      return {
        massId: resolvedMassId,
        interval: resolvedInterval,
        dateRange: resolvedRange,
      };
    }
    // Window dianchor ke data pulse terakhir aset bila diminta (bukan jam sekarang).
    if (anchorHoursFromLastData) {
      const anchored = anchorRangeToLastData(store, resolvedMassId, anchorHoursFromLastData);
      if (anchored) resolvedRange = anchored;
    }

    // Align start/end to the resolved interval boundaries so server aggregation
    // doesn't return buckets that fall outside the user's selected range.
    const fetchStart = moment(resolvedRange.start).startOf(resolvedInterval);
    const fetchEnd = moment(resolvedRange.end).endOf(resolvedInterval);
    await store.dispatch('asset/fetchSummary', {
      start: fetchStart,
      end: fetchEnd,
      interval: resolvedInterval,
      massId: resolvedMassId,
      splitByWeek,
    });
  }

  return {
    massId: resolvedMassId,
    interval: resolvedInterval,
    dateRange: resolvedRange,
  };
};
